import {
  Progress,
  Button,
  Card,
  CardBody,
  Avatar,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  Textarea,
  Skeleton,
} from "@heroui/react";
import { Link as RemixLink } from "@remix-run/react";
import { productComment, RateDistribution } from "app/api/product/product.type";
import { getAllProductComments, newProductReviewApi } from "app/api/product/products.api";
import { memo, useEffect, useState } from "react";
import { BiCommentDots } from "react-icons/bi";
import { MdStar } from "react-icons/md";
import { RxReload } from "react-icons/rx";

type FetchStatus = "SUCCESS" | "LOADING" | "IDLE" | "ERROR";

interface StarRatingProps {
  totalStars?: number;
  selectedStars: number;
  size?: number;
  onRate?: (rate: number) => void;
}
const StarRating = ({ totalStars = 5, selectedStars, size = 20, onRate }: StarRatingProps) => {
  return (
    <>
      {[...Array(totalStars)].map((_, i) => {
        const starNumber = i + 1;
        return (
          <MdStar
            key={i}
            size={size}
            className={`reviewRate text-gray-100 dark:text-zinc-500 dark:opacity-60 ${
              selectedStars >= i + 1 && " !text-[#fc0]"
            }`}
            onClick={onRate ? () => onRate(starNumber) : undefined}
            style={{
              cursor: onRate ? "pointer" : "default",
              // color: starNumber <= selectedStars ? activeColor : inactiveColor,
            }}
          />
        );
      })}
    </>
  );
};

interface SimpleModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  body: JSX.Element;
  footer: JSX.Element;
}
const SimpleModal = ({ isOpen, onClose, title, body, footer }: SimpleModalProps) => {
  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalContent>
        {(close) => (
          <>
            <ModalHeader className="flex flex-col gap-1 text-center w-full">{title}</ModalHeader>
            <ModalBody>{body}</ModalBody>
            <ModalFooter>{footer}</ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const CommentComponent = memo(function CommentComponent({
  productId,
  accessToken,
  userUid,
}: {
  productId: string;
  userUid?: string;
  accessToken?: string;
}) {
  // Modal disclosures (one for review form, one for login prompt)
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isUnAuthModalOpen,
    onOpen: onOpenUnAuthModal,
    onOpenChange: onCloseUnAuthModal,
  } = useDisclosure();

  const [selectedRate, setSelectedRate] = useState(0);
  const [comment, setComment] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [isLoading, setIsLoading] = useState(false);
  const [allReviews, setAllReviews] = useState<productComment[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>("IDLE");
  const [averageRate, setAverageRate] = useState(0);
  const [rateDistribution, setRateDistribution] = useState<RateDistribution>({
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0,
  });

  // Helper function to calculate average rating and distribution based on reviews data.
  const updateRatings = (data: productComment[]) => {
    const total = data.length;
    const sum = data.reduce((acc, { rating }) => acc + rating, 0);
    const avg = total ? Number((sum / total).toFixed(1)) : 0;
    setAverageRate(avg);

    const distribution: RateDistribution = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
    data.forEach(({ rating }) => {
      if (distribution[rating as keyof RateDistribution] !== undefined) {
        distribution[rating as keyof RateDistribution] += 1;
      }
    });
    setRateDistribution(distribution);
  };

  // Fetch reviews for a product
  const handleFetchReview = () => {
    setFetchStatus("LOADING");
    getAllProductComments(productId)
      .then(({ data }: { data: productComment[] }) => {
        setAllReviews(data);
        updateRatings(data);
        setFetchStatus("SUCCESS");
      })
      .catch(() => setFetchStatus("ERROR"));
  };

  // Submit a new review if the user is logged in; else show error message
  const submitReview = () => {
    if (!comment || !selectedRate) {
      return setErrorMessage("Review and rating are required");
    }
    if (userUid && accessToken) {
      setErrorMessage(null);
      setIsLoading(true);
      newProductReviewApi(
        { comment, productId, rating: selectedRate },
        {
          Authorization: `Bearer ${accessToken}`,
          "x-uid": userUid,
        },
      )
        .then(() => {
          setIsLoading(false);
          onOpenChange();
          handleFetchReview();
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  // Decide which modal to show: review form or login prompt.
  const handleAddReview = () => {
    if (userUid && accessToken) {
      onOpen();
    } else {
      onOpenUnAuthModal();
    }
  };

  useEffect(() => {
    handleFetchReview();
  }, []);

  return (
    <>
      <div className="p-2 rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800 my-2 cursor-pointer">
        <div className="flex flex-wrap md:flex-nowrap gap-2 justify-center sm:justify-between mb-4">
          <div>
            <div className="font-semibold text-4xl text-center">{averageRate}</div>
            <div className="flex items-center justify-center">
              <StarRating selectedStars={Math.round(averageRate)} size={20} />
            </div>
            <div className="text-center">{allReviews.length} Reviews</div>
          </div>
          <div className="w-full">
            {([5, 4, 3, 2, 1] as const).map((star, index) => {
              const count = rateDistribution[star];
              const percentage = allReviews.length ? (count / allReviews.length) * 100 : 0;
              return (
                <Progress
                  key={star}
                  label={`${star} star`}
                  aria-label={`${star} star`}
                  showValueLabel
                  formatOptions={{ style: "decimal" }}
                  valueLabel={count}
                  value={percentage}
                  className="max-w-md mb-2"
                />
              );
            })}
          </div>
        </div>

        <Button className="w-full" color="primary" onPress={handleAddReview}>
          Add a Review
        </Button>
      </div>

      <div>
        {fetchStatus === "SUCCESS" && (
          <div>
            {allReviews.map((review, index) => (
              <div key={index} className="rounded-xl bg-zinc-50 dark:bg-zinc-700 mb-3">
                <div className="flex flex-col w-full gap-2 p-4">
                  <div className="flex">
                    <div className="w-14">
                      <Avatar
                        name={
                          review?.author?.lastName?.charAt(0) + review?.author?.firstName?.charAt(0)
                        }
                        size={"md"}
                        color="primary"
                      />
                    </div>
                    <div className="w-full">
                      <div className="flex flex-col gap-2">
                        <div className="text-base font-semibold">
                          {review?.author?.lastName} {review?.author?.firstName}
                        </div>
                        <div className="flex justify-start gap-1">
                          <StarRating selectedStars={review?.rating} size={20} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="my-3">{review?.content}</div>
                  <div className="flex justify-end italic text-sm font-light text-zinc-500 dark:text-zinc-200">
                    {review?.createdAt}
                  </div>
                </div>
                {allReviews?.length > index + 1 && <Divider className="my-2" />}
              </div>
            ))}
          </div>
        )}
        {fetchStatus === "LOADING" && (
          <div className="my-4">
            {[...Array(5)].map((_, i) => (
              <div key={`s-${i}`} className="rounded-xl bg-gray-100/20 dark:bg-zinc-700 mb-3">
                <div className="flex flex-col w-full gap-2 p-4">
                  <div className="flex">
                    <div className="w-14">
                      <Skeleton className="flex rounded-full w-12 h-12" />
                    </div>
                    <div className="w-full">
                      <div className="flex flex-col justify-center gap-2">
                        <div className="flex gap-2 text-base font-semibold">
                          <Skeleton className="flex rounded-full w-12 h-2.5" />
                          <Skeleton className="flex rounded-full w-24 h-2.5" />
                        </div>
                        <div className="flex justify-start gap-1">
                          <StarRating selectedStars={0} size={20} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 my-1">
                    <Skeleton className="flex rounded-full w-70 h-2.5" />
                    <Skeleton className="flex rounded-full w-60 h-2.5" />
                    <Skeleton className="flex rounded-full w-24 h-2.5" />
                  </div>
                  <div className=" flex justify-end mt-1">
                    <Skeleton className="flex rounded-full w-20 h-2" />
                  </div>
                </div>
                {_?.length > i + 1 && <Divider className="my-2" />}
              </div>
            ))}
          </div>
        )}
        {fetchStatus === "ERROR" && (
          <div className="flex flex-col justify-center items-center gap-4 my-4">
            <div>An error occurred while fetching reviews.</div>
            <Button
              variant="flat"
              startContent={<RxReload />}
              color="primary"
              className="w-fit"
              onPress={handleFetchReview}
            >
              Reload
            </Button>
          </div>
        )}
      </div>

      {/* Modal for adding review */}
      <SimpleModal
        isOpen={isOpen}
        onClose={onOpenChange}
        title="Add a Review"
        body={
          <>
            <Textarea
              placeholder="Write a review"
              className="w-full"
              value={comment}
              onValueChange={setComment}
            />
            <div className="flex items-center justify-center my-4 reviewBox">
              <StarRating
                selectedStars={selectedRate}
                size={40}
                onRate={(rate) => setSelectedRate(rate)}
              />
            </div>
            {errorMessage && <div className="text-danger text-center">{errorMessage}</div>}
          </>
        }
        footer={
          <>
            <Button color="danger" variant="light" onPress={onOpenChange}>
              Close
            </Button>
            <Button color="primary" onPress={submitReview} isLoading={isLoading}>
              Submit
            </Button>
          </>
        }
      />

      {/* Modal for unauthorized review action with a message to login */}
      <SimpleModal
        isOpen={isUnAuthModalOpen}
        onClose={onCloseUnAuthModal}
        title="Log in to add a review"
        body={
          <div className="text-center">
            <div className="flex items-center justify-center bg-zinc-100 dark:bg-zinc-600 rounded-full h-14 w-14 mx-auto mb-4">
              <BiCommentDots size={32} />
            </div>
            <div>Got thoughts on this product?</div>
            <div>just log in to leave a review!</div>
            <div className="flex items-center justify-center my-4 ">
              <StarRating selectedStars={0} size={40} />
            </div>
          </div>
        }
        footer={
          <>
            <Button color="default" variant="light" onPress={onCloseUnAuthModal}>
              Close
            </Button>
            <Button color="primary" as={RemixLink} prefetch="intent" to="/login">
              Login
            </Button>
          </>
        }
      />
    </>
  );
});

export default CommentComponent;
